import React, { useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Pagination,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconFileExport,
  IconMail,
  IconPhone,
  IconRefresh,
  IconUserPlus,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { Endpoints } from "../../Lib/endpoints";
import { Colors } from "../../Lib/data";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getPaginationTotal,
} from "../../Lib/methods";
import { PaginatedResponse } from "../../Lib/responses";
import { PaginationProps, StudentProfileProps } from "../../Lib/types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import MegaLoader from "../../Components/MegaLoader";
import DisplayAlert from "../../Components/DisplayAlert";

const Waitlist: React.FC = () => {
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 12,
  });

  const {
    data: waitlist,
    isLoading,
    reloadData,
  } = usePerformRequest<PaginatedResponse<StudentProfileProps[]>>({
    method: Endpoints.GetWaitlist(pagination.page, pagination.limit).method,
    route: Endpoints.GetWaitlist(pagination.page, pagination.limit).route,
  });

  const [isExporting, setIsExporting] = useState(false);

  const splitName = (name?: string) => {
    const trimmedName = (name || "").trim();
    const [firstName = "", ...rest] = trimmedName.split(/\s+/).filter(Boolean);
    return {
      firstName,
      lastName: rest.join(" "),
    };
  };

  const formatAddress = (student: StudentProfileProps) =>
    [
      student.address?.street,
      student.address?.city,
      student.address?.zip,
      student.address?.country,
    ]
      .filter((value) => value && value.trim().length > 0)
      .join(", ");

  const escapeCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`;

  const exportWaitlist = async () => {
    setIsExporting(true);
    try {
      const response = await PerformRequest<PaginatedResponse<StudentProfileProps[]>>({
        method: Endpoints.GetWaitlist(1, 1000).method,
        route: Endpoints.GetWaitlist(1, 1000).route,
      });

      const dataToExport = response?.data?.data ?? [];
      if (!dataToExport.length) {
        DefaultErrorNotification("No waitlist entries available to export");
        return;
      }

      const csvHeaders = ["First Name", "Last Name", "Email", "Address"];
      const csvRows = dataToExport.map((student) => {
        const { firstName, lastName } = splitName(student.name);
        return [
          escapeCsvValue(firstName),
          escapeCsvValue(lastName),
          escapeCsvValue(student.email ?? ""),
          escapeCsvValue(formatAddress(student)),
        ];
      });

      const csvContent = [
        csvHeaders.join(","),
        ...csvRows.map((row) => row.join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `waitlist_export_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.csv`
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      DefaultSuccessNotification(
        `Successfully exported ${dataToExport.length} waitlist entr${dataToExport.length === 1 ? "y" : "ies"}`
      );
    } catch (error) {
      DefaultErrorNotification("An error occurred while exporting waitlist data");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Waitlist</Title>
            <Text c="dimmed" mt={6}>
              Leads collected from the student join page.
            </Text>
          </div>
          <Group>
            <Button
              leftSection={<IconFileExport size={16} />}
              variant="outline"
              color="green"
              onClick={exportWaitlist}
              loading={isExporting}
              disabled={isExporting}
            >
              {isExporting ? "Exporting..." : "Export CSV"}
            </Button>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              color={Colors.PurplePrimary}
              onClick={reloadData}
              loading={isLoading}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {isLoading ? (
          <MegaLoader />
        ) : waitlist?.data?.length ? (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
              {waitlist.data.map((student) => (
                <Card key={student.id} radius="xl" withBorder p="lg" shadow="sm">
                  <Stack gap="md">
                    <Group justify="space-between" align="flex-start">
                      <Group wrap="nowrap">
                        <Avatar radius="xl" size="lg" color="violet">
                          {student.name?.slice(0, 2).toUpperCase()}
                        </Avatar>
                        <div>
                          <Text fw={700}>{student.name}</Text>
                          <Text size="sm" c="dimmed">
                            Joined {dayjs(student.createdAt).format("DD MMM YYYY")}
                          </Text>
                        </div>
                      </Group>
                      <Badge variant="light" color="orange">
                        Waitlist
                      </Badge>
                    </Group>

                    <Group wrap="nowrap" gap="xs">
                      <ThemeIcon radius="xl" variant="light" color="blue">
                        <IconMail size={14} />
                      </ThemeIcon>
                      <Text size="sm" lineClamp={1}>
                        {student.email}
                      </Text>
                    </Group>

                    <Group wrap="nowrap" gap="xs">
                      <ThemeIcon radius="xl" variant="light" color="teal">
                        <IconPhone size={14} />
                      </ThemeIcon>
                      <Text size="sm">{student.phone || "No phone provided"}</Text>
                    </Group>

                    <Group wrap="nowrap" gap="xs">
                      <ThemeIcon radius="xl" variant="light" color="grape">
                        <IconUserPlus size={14} />
                      </ThemeIcon>
                      <Text size="sm">
                        {student.address?.country || "Country not provided"}
                      </Text>
                    </Group>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>

            <Group justify="center" mt="md">
              <Pagination
                value={pagination.page}
                onChange={(page) => setPagination({ ...pagination, page })}
                total={getPaginationTotal(
                  waitlist.totalRecords,
                  pagination.limit
                )}
                disabled={isLoading}
              />
            </Group>
          </>
        ) : (
          <DisplayAlert
            title="No waitlist entries"
            children={
              <Text>
                No one has submitted the join form yet.
              </Text>
            }
          />
        )}
      </Stack>
    </Container>
  );
};

export default Waitlist;
